import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, Users, Calendar as CalIcon, User, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import Avatar from "@/components/Avatar";
import AddToGroupDropdown from "@/components/AddToGroupDropdown";
import { useNotifications } from "@/contexts/NotificationContext";

/**
 * SearchPalette — universal ⌘K-style search across People / Groups / Events.
 *
 * Opens via the three nav buttons (Groups / Events / People). The triggering
 * button selects the initial tab; the user can switch tabs at any time.
 *
 * People search is GLOBAL (so a group owner can invite anyone signed up).
 * Each people row gets an "Add to…" picker listing the searcher's OWNED
 * groups; clicking a group creates a pending invite that lands as a
 * personalised notification on the target user's AlertBar
 * (e.g. "Steve invited you to 'Birthday Planning Committee'").
 *
 * Groups + Events search is scoped to the user's reachable surface.
 */
const TABS = [
  { key: "people", label: "People", icon: User },
  { key: "groups", label: "Groups", icon: Users },
  { key: "events", label: "Events", icon: CalIcon },
];

export default function SearchPalette({ open, initialTab = "people", onClose }) {
  const [tab, setTab] = useState(initialTab);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState({ people: [], groups: [], events: [] });
  const [ownedGroups, setOwnedGroups] = useState([]);
  const [inviteBusyKey, setInviteBusyKey] = useState(null);
  const [flash, setFlash] = useState("");
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const { refresh: refreshNotifs } = useNotifications();

  // Re-seed the active tab whenever the palette re-opens.
  const [seenOpen, setSeenOpen] = useState(open);
  if (open !== seenOpen) {
    setSeenOpen(open);
    if (open) {
      setTab(initialTab);
      setQ("");
      setResults({ people: [], groups: [], events: [] });
      setFlash("");
    }
  }

  useEffect(() => {
    if (open && inputRef.current) inputRef.current.focus();
  }, [open]);

  // Pull the user's owned groups once on first open so the "Add to…" picker
  // doesn't need a roundtrip per row.
  useEffect(() => {
    if (!open || ownedGroups.length > 0) return;
    (async () => {
      try {
        const { data } = await api.get("/groups");
        // Backend doesn't tag is_owner here; we infer by owner_id === me later
        // via the /auth/me caller. Simpler: just list all groups the user is
        // in, since membership lets you invite too.
        setOwnedGroups(data || []);
      } catch (e) {
        console.warn("[search] owned groups fetch:", e?.message || e);
      }
    })();
  }, [open, ownedGroups.length]);

  // Debounced search.
  useEffect(() => {
    if (!open) return;
    const term = q.trim();
    if (term.length < 2) {
      setResults({ people: [], groups: [], events: [] });
      return;
    }
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const { data } = await api.get(`/search?q=${encodeURIComponent(term)}&kind=${tab}`);
        setResults(data || { people: [], groups: [], events: [] });
      } catch (e) {
        console.warn("[search] failed:", e?.message || e);
      } finally {
        setBusy(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [q, tab, open]);

  if (!open) return null;

  const sendInvite = async (person, group) => {
    const key = `${person.user_id}-${group.group_id}`;
    setInviteBusyKey(key);
    setFlash("");
    try {
      await api.post("/invites", { target_user_id: person.user_id, group_id: group.group_id });
      setFlash(`Invited ${person.name || person.email} to "${group.name}". They'll see it as an alert.`);
      refreshNotifs && refreshNotifs();
    } catch (e) {
      setFlash(formatError(e));
    } finally {
      setInviteBusyKey(null);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-start p-4 pt-16"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="search-palette"
    >
      <div
        className="bd-surface w-full max-w-2xl"
        style={{ borderRadius: "var(--radius-card)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3" style={{ borderBottom: "1px solid var(--border)" }}>
          <Search size={16} style={{ color: "var(--text-dim)" }} />
          <input
            ref={inputRef}
            className="flex-1 bg-transparent outline-none text-base"
            placeholder={`Search ${TABS.find((t) => t.key === tab)?.label.toLowerCase()}…`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            data-testid="search-input"
            style={{ color: "var(--text)" }}
          />
          {busy && <Loader2 size={14} className="animate-spin" />}
          <button
            onClick={onClose}
            className="bd-btn bd-btn-ghost"
            style={{ padding: "0.3rem" }}
            data-testid="search-close"
            aria-label="Close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="flex" style={{ borderBottom: "1px solid var(--border)" }}>
          {TABS.map((t) => {
            const active = t.key === tab;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                className="flex-1 flex items-center justify-center gap-1.5 text-sm py-2"
                style={{
                  background: active ? "var(--bg-soft)" : "transparent",
                  color: active ? "var(--text)" : "var(--text-dim)",
                  borderBottom: active ? "2px solid var(--primary)" : "2px solid transparent",
                  border: "none", cursor: "pointer",
                }}
                data-testid={`search-tab-${t.key}`}
              >
                <t.icon size={12} /> {t.label}
              </button>
            );
          })}
        </div>

        {flash && (
          <div className="text-xs px-4 py-2"
               style={{ background: "color-mix(in srgb, var(--primary) 12%, transparent)", color: "var(--text)" }}
               data-testid="search-flash">
            {flash}
          </div>
        )}

        <div className="max-h-[60vh] overflow-y-auto p-2">
          {q.trim().length < 2 && (
            <div className="text-sm py-8 text-center" style={{ color: "var(--text-dim)" }}>
              Type at least 2 characters to search.
            </div>
          )}

          {tab === "people" && results.people.map((p) => (
            <div
              key={p.user_id}
              className="flex items-center gap-3 px-3 py-2 rounded mb-1"
              style={{ border: "1px solid var(--border)" }}
              data-testid={`search-people-${p.user_id}`}
            >
              <Avatar name={p.name || p.email} picture={p.picture} size={36} />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.name || p.email}</div>
                <div className="text-xs truncate" style={{ color: "var(--text-dim)" }}>{p.email}</div>
              </div>
              <AddToGroupDropdown
                ownedGroups={ownedGroups}
                onPick={(g) => sendInvite(p, g)}
                busyKey={inviteBusyKey}
                userId={p.user_id}
                testIdPrefix={`search-invite-${p.user_id}`}
              />
            </div>
          ))}

          {tab === "groups" && results.groups.map((g) => (
            <button
              key={g.group_id}
              onClick={() => { onClose(); navigate(`/groups`); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded mb-1 text-left"
              style={{ border: "1px solid var(--border)", background: "transparent", cursor: "pointer" }}
              data-testid={`search-group-${g.group_id}`}
            >
              <div className="w-8 h-8 grid place-items-center rounded"
                   style={{ background: "var(--primary)", color: "var(--primary-fg)" }}>
                <Users size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">
                  {g.name}
                  {g.is_owner && <span className="ml-2 bd-chip" style={{ fontSize: "0.55rem" }}>Owner</span>}
                  {g.scheduling_mode && <span className="ml-1 bd-chip" style={{ fontSize: "0.55rem" }}>Scheduling</span>}
                </div>
                <div className="text-xs" style={{ color: "var(--text-dim)" }}>
                  {g.member_count} member{g.member_count === 1 ? "" : "s"}
                </div>
              </div>
            </button>
          ))}

          {tab === "events" && results.events.map((ev) => (
            <button
              key={ev.event_id}
              onClick={() => { onClose(); navigate(`/events/${ev.event_id}`); }}
              className="w-full flex items-center gap-3 px-3 py-2 rounded mb-1 text-left"
              style={{ border: "1px solid var(--border)", background: "transparent", cursor: "pointer" }}
              data-testid={`search-event-${ev.event_id}`}
            >
              <div className="w-8 h-8 grid place-items-center rounded"
                   style={{ background: "color-mix(in srgb, var(--primary) 35%, transparent)" }}>
                <CalIcon size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{ev.title}</div>
                <div className="text-xs bd-mono" style={{ color: "var(--text-dim)" }}>
                  {ev.date} · {ev.event_type}
                </div>
              </div>
            </button>
          ))}

          {q.trim().length >= 2 && !busy && (
            (tab === "people" && results.people.length === 0) ||
            (tab === "groups" && results.groups.length === 0) ||
            (tab === "events" && results.events.length === 0)
          ) && (
            <div className="text-sm py-6 text-center" style={{ color: "var(--text-dim)" }} data-testid="search-empty">
              Not found — share Born Day with them first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
